import type {Component} from "vue";
import HtmlDashboardWidget from "@/components/dashboardWidgets/HtmlDashboardWidget.vue";
import ReportDashboardWidget from "@/components/dashboardWidgets/ReportDashboardWidget.vue";
import CardMenuDashboardWidget from "@/components/dashboardWidgets/CardMenuDashboardWidget.vue";
import MetricsCardsDashboardWidget from "@/components/dashboardWidgets/MetricsCardDashboardWidget.vue";
import EnhancedMetricsCardDashboardWidget from "@/components/dashboardWidgets/EnhancedMetricsCardDashboardWidget.vue";
import TimelineDashboardWidget from "@/components/dashboardWidgets/TimelineDashboardWidget.vue";
import RecentVisitsDashboardWidget from "@/components/dashboardWidgets/RecentVisitsDashboardWidget.vue";
import TableDashboardWidget from "@/components/dashboardWidgets/TableDashboardWidget.vue";
import EnhancedTableDashboardWidget from "@/components/dashboardWidgets/EnhancedTableDashboardWidget.vue";
import CalendarDashboardWidget from "@/components/dashboardWidgets/CalendarDashboardWidget.vue";
import EChartWidget from "@/components/dashboardWidgets/EChartWidget.vue";
import CrossFilterControlWidget from "@/components/dashboardWidgets/CrossFilterControlWidget.vue";
import TextBlockWidget from "@/components/dashboardWidgets/TextBlockWidget.vue";
import DTOUserDashbordChartLine = namaTypes.DTOUserDashbordChartLine;

export interface DashboardWidgetDefinition {
  component: Component;
  isScheduler?: boolean;
  isThreeD?: boolean;
}

// Single source of truth for chartType → widget component, shared by the dashboard page and the drill-down popup.
export const dashboardWidgets: Record<string, DashboardWidgetDefinition> = {
  PieChart: {component: EChartWidget},
  ThreeDPieChart: {component: EChartWidget},
  ColumnWithRotatedLabels: {component: EChartWidget},
  ColumnWithCategsAndLabels: {component: EChartWidget},
  CombinationChart: {component: EChartWidget},
  BasicAreaChart: {component: EChartWidget},
  Table: {component: TableDashboardWidget},
  EnhancedTable: {component: EnhancedTableDashboardWidget},
  Gauge: {component: EChartWidget},
  HeatMap: {component: EChartWidget},
  HTML: {component: HtmlDashboardWidget},
  ColumnRange: {component: EChartWidget},
  Calendar: {component: CalendarDashboardWidget, isScheduler: false},
  ResourceView: {component: CalendarDashboardWidget, isScheduler: true},
  Report: {component: ReportDashboardWidget},
  StackedAndGroupedColumn: {component: EChartWidget},
  CardMenu: {component: CardMenuDashboardWidget},
  MetricsCards: {component: MetricsCardsDashboardWidget},
  EnhancedMetricsCard: {component: EnhancedMetricsCardDashboardWidget},
  Timeline: {component: TimelineDashboardWidget},
  RecentVisits: {component: RecentVisitsDashboardWidget},
  CrossFilterControl: {component: CrossFilterControlWidget},
  TextBlock: {component: TextBlockWidget},
  EChart: {component: EChartWidget}
};

const AUTO_HEIGHT_WIDGET_TYPES = new Set<string>([
  'MetricsCards',
  'EnhancedMetricsCard',
  'CrossFilterControl',
  'CardMenu',
  'TextBlock',
  'RecentVisits',
]);

// Widgets drawing their own admin open-widget button; the rest get the dashboard's floating one.
const WIDGETS_WITH_OWN_OPEN_BUTTON = new Set<Component>([
  EChartWidget,
  TableDashboardWidget,
  EnhancedTableDashboardWidget,
  CardMenuDashboardWidget,
  MetricsCardsDashboardWidget,
  EnhancedMetricsCardDashboardWidget,
]);

export function getDashboardWidgetDefinition(line: DTOUserDashbordChartLine): DashboardWidgetDefinition | undefined {
  if (!line.chartType) return undefined;
  return dashboardWidgets[line.chartType];
}

export function getDashboardWidgetComponent(line: DTOUserDashbordChartLine): Component {
  return getDashboardWidgetDefinition(line)?.component ?? EChartWidget;
}

export function isEChartWidget(line: DTOUserDashbordChartLine): boolean {
  return getDashboardWidgetDefinition(line)?.component === EChartWidget;
}

export function hasOwnOpenWidgetButton(line: DTOUserDashbordChartLine): boolean {
  const component = getDashboardWidgetDefinition(line)?.component;
  return !!component && WIDGETS_WITH_OWN_OPEN_BUTTON.has(component);
}

export function isAutoHeightWidget(line: DTOUserDashbordChartLine): boolean {
  return !!line.chartType && AUTO_HEIGHT_WIDGET_TYPES.has(line.chartType);
}

// CSS grid-area value "rowStart / colStart / rowEnd / colEnd" for a dashboard line; `rows` lets a caller
// override the span (e.g. a collapsed widget occupies one row).
export function widgetGridArea(line: DTOUserDashbordChartLine, rows: number = line.rows ?? 1): string {
  const rowStart = line.rowNumber ?? 1;
  const colStart = line.colNumber ?? 1;
  return rowStart + '/' + colStart + '/' + (rowStart + rows) + '/' + (colStart + (line.cols ?? 1));
}

export function sortChartLines(chartLines: DTOUserDashbordChartLine[] | undefined): DTOUserDashbordChartLine[] {
  if (!chartLines)
    return [];
  chartLines.sort((a, b) => {
    if (a.rowNumber === b.rowNumber)
      return (a.colNumber ?? 0) - (b.colNumber ?? 0);
    return (a.rowNumber ?? 0) - (b.rowNumber ?? 0);
  });
  return chartLines;
}
